// ─────────────────────────────────────────────────────────────────────────────
// graphShaders — the node and edge programs.
//
// ShaderMaterial, never PointsMaterial: the graph needs per-point size, alpha,
// focus and pulse, and a soft core-plus-halo falloff that a sprite texture would
// only approximate.
//
// depthTest STAYS TRUE. This is a real volume with a front and a back — the orb
// can disable depth because it is a shell, but a galaxy you fly INTO must place
// rear nodes behind front ones. If a dense field ever reads as fog, the answer
// is not to disable depth and not to enlarge nodes or halos. Report it first.
//
// Colour never appears in this file. It arrives per-vertex from the palette, so
// a re-ruled domain colour is a buffer rebuild and not a shader edit.
// ─────────────────────────────────────────────────────────────────────────────

export const NODE_VERTEX_SHADER = `
attribute vec3 aColor;
attribute float aSize;
attribute float aAlpha;
attribute float aImportance;
attribute float aSelected;
attribute float aHovered;
attribute float aContextAlpha;
attribute float aPulsePhase;

uniform float uPixelRatio;
uniform float uReferenceDistance;
uniform float uTime;
uniform float uSizeMultiplier;
uniform float uMaxPointSize;

varying vec3 vColor;
varying float vAlpha;
varying float vSelected;
varying float vHovered;

void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;

  float depthScale = clamp(uReferenceDistance / max(-mv.z, 1.0), 0.58, 1.85);

  float ambient = 1.0 + sin(uTime * 0.55 + aPulsePhase) * 0.018;

  float emphasis =
    1.0 + aImportance * 0.16 + aSelected * 0.42 + aHovered * 0.18;

  float pointSize =
    aSize * uSizeMultiplier * ambient * emphasis * depthScale * uPixelRatio;

  gl_PointSize = clamp(pointSize, 1.5, uMaxPointSize);

  vColor = aColor;
  vAlpha = aAlpha * aContextAlpha;
  vSelected = aSelected;
  vHovered = aHovered;
}
`;

export const NODE_FRAGMENT_SHADER = `
precision mediump float;

uniform float uMidWeight;
uniform float uHaloWeight;
uniform float uWhiteMix;
uniform float uExposure;

varying vec3 vColor;
varying float vAlpha;
varying float vSelected;
varying float vHovered;

void main() {
  vec2 uv = gl_PointCoord * 2.0 - 1.0;
  float d = length(uv);
  if (d > 1.0) discard;

  float pin  = 1.0 - smoothstep(0.025, 0.275, d);
  float mid  = 1.0 - smoothstep(0.10,  0.60,  d);
  float halo = 1.0 - smoothstep(0.42,  1.0,   d);

  float focus = clamp(vSelected * 0.55 + vHovered * 0.22, 0.0, 1.0);

  float alpha =
    pin  * vAlpha * 1.00 +
    mid  * vAlpha * uMidWeight +
    halo * vAlpha * uHaloWeight;

  alpha *= 1.0 + focus * 0.30;

  if (alpha < 0.0015) discard;

  vec3 hot = mix(vColor, vec3(1.0), pin * (uWhiteMix + focus * 0.36));
  vec3 colour = mix(vColor, hot, pin) * uExposure;

  gl_FragColor = vec4(colour, alpha);
}
`;

// ── edges ─────────────────────────────────────────────────────────────────────
// One LineSegments per chunk, never one Line per edge. Each vertex carries its
// own end's colour so the segment interpolates between the two boroughs it
// joins, and its own alpha so the hierarchy ladder (spine bright, leaves almost
// invisible) is data rather than a uniform.
//
// uAlphaScale is the overlay's base-link-opacity control in G1. It is a single
// uniform on purpose: turning that slider must not rebuild a buffer.

export const EDGE_VERTEX_SHADER = `
attribute vec3 aEdgeColor;
attribute float aEdgeAlpha;
attribute float aRelationKind;

uniform float uAlphaScale;

varying vec3 vColor;
varying float vAlpha;

void main() {
  vColor = aEdgeColor;
  vAlpha = aEdgeAlpha * uAlphaScale;

  gl_Position =
    projectionMatrix *
    modelViewMatrix *
    vec4(position, 1.0);
}
`;

export const EDGE_FRAGMENT_SHADER = `
precision mediump float;

varying vec3 vColor;
varying float vAlpha;

void main() {
  if (vAlpha < 0.0015) {
    discard;
  }

  gl_FragColor =
    vec4(
      vColor,
      vAlpha
    );
}
`;


// ── edge sparks ───────────────────────────────────────────────────────────────
// Samples of REAL edges, not graph records: no ids, no picking, no labels, no
// store entry. Each point sits on the segment between two nodes that actually
// relate, which is why adding them is density rather than decoration.
//
// Same pin language as the nodes, with tighter limits — sparks are the connective
// haze between structures and must never compete with a hub.

export const SPARK_VERTEX_SHADER = `
attribute vec3 aColor;
attribute float aSize;
attribute float aAlpha;
attribute float aPulsePhase;

uniform float uPixelRatio;
uniform float uReferenceDistance;
uniform float uTime;
uniform float uOpacity;
uniform float uMaxPointSize;

varying vec3 vColor;
varying float vAlpha;

void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;

  float depthScale = clamp(uReferenceDistance / max(-mv.z, 1.0), 0.55, 1.7);

  // ±3% only. Enough to keep the field alive, far too little to read as motion.
  float ambient = 1.0 + sin(uTime * 0.42 + aPulsePhase) * 0.03;

  gl_PointSize = clamp(aSize * depthScale * uPixelRatio, 1.0, uMaxPointSize);

  vColor = aColor;
  vAlpha = aAlpha * uOpacity * ambient;
}
`;

export const SPARK_FRAGMENT_SHADER = `
precision mediump float;

uniform float uMidWeight;
uniform float uHaloWeight;
uniform float uWhiteMix;
uniform float uExposure;

varying vec3 vColor;
varying float vAlpha;

void main() {
  vec2 uv = gl_PointCoord * 2.0 - 1.0;
  float d = length(uv);
  if (d > 1.0) discard;

  float pin  = 1.0 - smoothstep(0.03, 0.30, d);
  float mid  = 1.0 - smoothstep(0.12, 0.62, d);
  float halo = 1.0 - smoothstep(0.45, 1.0,  d);

  float alpha =
    pin * vAlpha * 1.00 + mid * vAlpha * uMidWeight + halo * vAlpha * uHaloWeight;

  if (alpha < 0.0012) discard;

  vec3 hot = mix(vColor, vec3(1.0), pin * uWhiteMix);
  gl_FragColor = vec4(mix(vColor, hot, pin) * uExposure, alpha);
}
`;
